import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiClientError, refreshAccessToken, setUnauthorizedHandler } from "../services/api-client";
import { fetchAdminMe, loginRequest, logoutRequest, type AdminUser } from "./auth.api";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./tokenStore";

/**
 * checking     - restoring a session after a page reload
 * anonymous    - nobody signed in
 * authenticated- a verified admin
 * unavailable  - the server could not be reached while restoring; the saved session is kept
 */
export type AuthStatus = "checking" | "anonymous" | "authenticated" | "unavailable";

export class NotAdminError extends Error {
  constructor() {
    super("Akun ini bukan admin");
    this.name = "NotAdminError";
  }
}

interface AuthContextValue {
  status: AuthStatus;
  user: AdminUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  retry: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** The server did not decide anything about the session: do not log the admin out for it. */
const isTransient = (err: unknown): boolean =>
  err instanceof ApiClientError &&
  (err.code === "NETWORK_ERROR" || err.statusCode >= 500 || err.statusCode === 429 || err.statusCode === 408);

/** Ends this tab's session on the server. Never throws: an unreachable server must not trap the admin. */
async function endServerSession(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return;
  try {
    await logoutRequest(refreshToken);
  } catch {
    // The local sign-out is what matters.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    getRefreshToken() || getAccessToken() ? "checking" : "anonymous",
  );
  const [user, setUser] = useState<AdminUser | null>(null);

  const signOutLocally = useCallback(() => {
    clearTokens();
    setUser(null);
    setStatus("anonymous");
  }, []);

  const restore = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (!getRefreshToken() && !getAccessToken()) {
        setStatus("anonymous");
        return;
      }
      setStatus("checking");
      try {
        if (!getAccessToken()) await refreshAccessToken();
        const me = await fetchAdminMe();
        if (isCancelled()) return;
        if (me.role !== "admin") return signOutLocally();
        setUser(me);
        setStatus("authenticated");
      } catch (err) {
        if (isCancelled()) return;
        if (isTransient(err)) setStatus("unavailable");
        else signOutLocally();
      }
    },
    [signOutLocally],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus("anonymous");
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void restore(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [restore]);

  const login = useCallback(async (username: string, password: string) => {
    const session = await loginRequest(username, password);
    setTokens({ accessToken: session.token, refreshToken: session.refreshToken });
    let me: AdminUser;
    try {
      me = await fetchAdminMe();
    } catch (err) {
      // Sessions are per device: ending this one cannot sign the person out of their phone, and
      // leaving it alive would hold one of their 5 session slots for nothing.
      if (err instanceof ApiClientError && err.code === "ADMIN_REQUIRED") {
        await endServerSession();
        clearTokens();
        throw new NotAdminError();
      }
      clearTokens();
      throw err;
    }
    if (me.role !== "admin") {
      await endServerSession();
      clearTokens();
      throw new NotAdminError();
    }
    setUser(me);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await endServerSession();
    signOutLocally();
  }, [signOutLocally]);

  const retry = useCallback(() => restore(), [restore]);

  const value = useMemo(() => ({ status, user, login, logout, retry }), [status, user, login, logout, retry]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth harus dipakai di dalam AuthProvider");
  return ctx;
}
